// Chat app API — `/api/admin/my/chat/*`, open to every tenant role.

import type {
  AgentEvaluation,
  AnswerRating,
  EvaluationSummary,
  ChatConversation,
  ChatConversationDetail,
  ChatSendResult,
  EvaluationSettings,
  RatingsPage,
  ReasonCategory,
  StoreAgent,
} from "@/types/api";

import { apiFetch } from "./client";

const base = "/admin/my/chat";

export const listStoreAgents = () => apiFetch<StoreAgent[]>(`${base}/agents`);

export const listConversations = () => apiFetch<ChatConversation[]>(`${base}/conversations`);

export const createConversation = (agentSlug: string) =>
  apiFetch<ChatConversation>(`${base}/conversations`, {
    method: "POST",
    body: { agent_slug: agentSlug },
  });

export const getConversation = (id: string) =>
  apiFetch<ChatConversationDetail>(`${base}/conversations/${id}`);

export const renameConversation = (id: string, title: string) =>
  apiFetch<ChatConversation>(`${base}/conversations/${id}`, { method: "PATCH", body: { title } });

export const deleteConversation = (id: string) =>
  apiFetch<void>(`${base}/conversations/${id}`, { method: "DELETE" });

export const sendChatMessage = (id: string, content: string) =>
  apiFetch<ChatSendResult>(`${base}/conversations/${id}/messages`, {
    method: "POST",
    body: { content },
  });

// ---- answer ratings (AVI-171) --------------------------------------------------

export const rateAnswer = (
  conversationId: string,
  messageId: number,
  body: { rating: "up" | "down"; category?: ReasonCategory | null; reason?: string | null },
) =>
  apiFetch<AnswerRating>(`${base}/conversations/${conversationId}/messages/${messageId}/rating`, {
    method: "PUT",
    body,
  });

export const clearAnswerRating = (conversationId: string, messageId: number) =>
  apiFetch<void>(`${base}/conversations/${conversationId}/messages/${messageId}/rating`, {
    method: "DELETE",
  });

export type RatingFilter = "down" | "up" | "all";
/** Builders' review queue. Default: thumbs-downs. */
export const listRatings = (filters: {
  rating: RatingFilter;
  agent?: string;
  version?: string;
  days?: number;
}) => {
  const q = new URLSearchParams({ rating: filters.rating });
  if (filters.agent) q.set("agent", filters.agent);
  if (filters.version) q.set("version", filters.version);
  if (filters.days) q.set("days", String(filters.days));
  return apiFetch<RatingsPage>(`/admin/my/ratings?${q.toString()}`);
};

/** The Evaluations overview: approval across agents and a card per agent. */
export const getEvaluationSummary = (days: number) =>
  apiFetch<EvaluationSummary>(`/admin/my/ratings/summary?days=${days}`);

/** One agent's evaluation: trend, by version, by reason. */
export const getAgentEvaluation = (slug: string, days: number) =>
  apiFetch<AgentEvaluation>(`/admin/my/ratings/agents/${encodeURIComponent(slug)}?days=${days}`);

// ---- org admin: automatic answer scoring (AVI-170) -------------------------------

export const getEvaluationSettings = () =>
  apiFetch<EvaluationSettings>("/admin/my/evaluation-settings");

export const putEvaluationSettings = (input: EvaluationSettings) =>
  apiFetch<EvaluationSettings>("/admin/my/evaluation-settings", { method: "PUT", body: input });

// ---- org admin: every conversation in the org ----------------------------------

export const listOrgConversations = (userEmail?: string) =>
  apiFetch<ChatConversation[]>(
    `${base}/org/conversations${userEmail ? `?user_email=${encodeURIComponent(userEmail)}` : ""}`,
  );

export const getOrgConversation = (id: string) =>
  apiFetch<ChatConversationDetail>(`${base}/org/conversations/${id}`);
